import React, {
 createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useRequest } from '@common/services/api';
import { AlertService } from '@common/services/alert';

import type { CustomUserPropertiesResponse, CustomUserProperty } from '../types';
import {
  buildCustomUserPropertyPredicateFilters,
  type CustomUserPropertyPredicateFilter,
} from '../utils/build-custom-user-property-predicate-filters';

type CustomUserPropertiesContextValue = {
  organisationId: string | undefined;
  enabled: boolean;
  properties: CustomUserProperty[];
  propertiesById: Map<string, CustomUserProperty>;
  filtersMap: Record<string, CustomUserPropertyPredicateFilter>;
  loading: boolean;
  /** True after the definitions request has finished (success or error). */
  ready: boolean;
  error: Error | null;
};

const EMPTY_PROPERTIES_BY_ID = new Map<string, CustomUserProperty>();
const EMPTY_FILTERS_MAP: Record<string, CustomUserPropertyPredicateFilter> = {};

const CustomUserPropertiesContext = createContext<CustomUserPropertiesContextValue | null>(null);

export function useCustomUserPropertiesContextOptional() {
  return useContext(CustomUserPropertiesContext);
}

export const CustomUserPropertiesProvider = memo(({
  organisationId,
  enabled = false,
  children,
}: React.PropsWithChildren<{ organisationId: string | undefined; enabled?: boolean }>) => {
  const { t } = useTranslation();

  const endpoint = useMemo(
    () => (organisationId ? `/v1/organisations/${organisationId}/user-properties` : ''),
    [organisationId]
  );

  const handleLoadError = useCallback(() => {
    AlertService.error(t('organisation:user_properties_load_error'));
  }, [t]);

  const {
    data: properties = [],
    loading,
    error,
    reset,
  } = useRequest<CustomUserPropertiesResponse>(endpoint, {
    enabled: enabled && !!organisationId,
    onError: handleLoadError,
  });

  // useRequest reports loading=false until its effect starts, so an empty list is not "fetched empty" yet.
  // It also fetches only once, so an organisation switch must reset the request and readiness together.
  const sawLoadingRef = useRef(false);
  const previousOrganisationIdRef = useRef<string | undefined>();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled || !organisationId) {
      sawLoadingRef.current = false;
      previousOrganisationIdRef.current = organisationId;
      setReady(false);
      return;
    }

    const previousOrganisationId = previousOrganisationIdRef.current;
    const organisationChanged = (
      !!previousOrganisationId && previousOrganisationId !== organisationId
    );
    previousOrganisationIdRef.current = organisationId;

    if (organisationChanged) {
      sawLoadingRef.current = false;
      setReady(false);
      reset();
      return;
    }

    if (loading) {
      sawLoadingRef.current = true;
      return;
    }

    if (sawLoadingRef.current) {
      setReady(true);
    }
    // eslint-disable-next-line -- reset identity is unstable in useRequest
  }, [enabled, organisationId, loading]);

  const filtersMap = useMemo(
    () => (enabled ? buildCustomUserPropertyPredicateFilters(properties) : EMPTY_FILTERS_MAP),
    [enabled, properties]
  );

  const propertiesById = useMemo(
    () => (enabled
      ? new Map(properties.map((property) => [property.id, property]))
      : EMPTY_PROPERTIES_BY_ID),
    [enabled, properties]
  );

  const value = useMemo<CustomUserPropertiesContextValue>(() => ({
    organisationId,
    enabled,
    properties: enabled ? properties : [],
    propertiesById,
    filtersMap,
    loading: enabled ? loading : false,
    ready: enabled ? ready : false,
    error: enabled ? ((error as Error | null) ?? null) : null,
  }), [organisationId, enabled, properties, propertiesById, filtersMap, loading, ready, error]);

  return (
    <CustomUserPropertiesContext.Provider value={value}>
      {children}
    </CustomUserPropertiesContext.Provider>
  );
});

CustomUserPropertiesProvider.displayName = 'CustomUserPropertiesProvider';

